// js/complaintForm.js
// ===================================
// COMPLAINT FORM HANDLER (Libro de Reclamaciones) - FRONTEND
// ===================================
import { notifications } from "./notifications.js";

class ComplaintFormManager {
  constructor() {
    this.form = null;
    this.submitBtn = null;
    this.btnText = null;
    this.btnLoading = null;
    this.messageContainer = null;
    this.minorCheckbox = null;
    this.guardianFields = null;
    this.isSubmitting = false;
  }

  init() {
    this.form = document.getElementById("complaintForm");
    if (!this.form) return;

    this.submitBtn = document.getElementById("complaint-submit-btn");
    this.btnText = document.getElementById("complaint-btn-text");
    this.btnLoading = document.getElementById("complaint-btn-loading");
    this.messageContainer = document.getElementById("complaint-form-message");
    this.minorCheckbox = document.getElementById("is_minor");
    this.guardianFields = document.getElementById("guardian-fields");

    this.form.addEventListener("submit", (e) => this.handleSubmit(e));

    if (this.minorCheckbox && this.guardianFields) {
      this.minorCheckbox.addEventListener("change", () => {
        this.guardianFields.style.display = this.minorCheckbox.checked
          ? "grid"
          : "none";
      });
    }
  }

  collectFormData() {
    const formData = new FormData(this.form);
    return {
      complaint_type: formData.get("complaint_type"),
      full_name: formData.get("full_name")?.trim(),
      document_type: formData.get("document_type"),
      document_number: formData.get("document_number")?.trim(),
      address: formData.get("address")?.trim() || "",
      phone: formData.get("phone")?.trim(),
      email: formData.get("email")?.trim(),
      is_minor: !!formData.get("is_minor"),
      guardian_name: formData.get("guardian_name")?.trim() || "",
      guardian_document: formData.get("guardian_document")?.trim() || "",
      service_description: formData.get("service_description")?.trim(),
      service_date: formData.get("service_date") || "",
      claimed_amount: formData.get("claimed_amount")?.trim() || "",
      detail: formData.get("detail")?.trim(),
      request: formData.get("request")?.trim(),
      accepted_terms: !!formData.get("accepted_terms"),
    };
  }

  setLoadingState(isLoading) {
    this.submitBtn.disabled = isLoading;
    this.btnText.style.display = isLoading ? "none" : "inline";
    this.btnLoading.style.display = isLoading ? "inline" : "none";
    this.submitBtn.classList.toggle("opacity-75", isLoading);
    this.submitBtn.classList.toggle("cursor-not-allowed", isLoading);
  }

  showMessage(message, type = "error") {
    notifications.show(message, type);

    if (!this.messageContainer) return;
    this.messageContainer.style.display = "block";
    this.messageContainer.className =
      "p-4 rounded-lg text-center font-semibold transition-all duration-300 bg-red-50 text-red-800 border border-red-200";
    this.messageContainer.textContent = message;
    this.messageContainer.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str ?? "";
    return div.innerHTML;
  }

  showReceipt(data, result) {
    notifications.show(result.message, "success");

    if (!this.messageContainer) return;
    const typeLabel = data.complaint_type === "reclamo" ? "Reclamo" : "Queja";
    const esc = (s) => this.escapeHtml(s);

    this.messageContainer.style.display = "block";
    this.messageContainer.className =
      "p-6 text-left rounded-xl bg-green-50 border-2 border-green-300";
    this.messageContainer.innerHTML = `
      <div class="flex items-start gap-3 mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="flex-shrink-0 w-8 h-8 text-green-600" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
        </svg>
        <div>
          <p class="text-lg font-bold text-green-900">${typeLabel} registrado correctamente</p>
          <p class="text-sm text-green-800">Te responderemos dentro de un máximo de 15 días hábiles. Hemos enviado la confirmación a ${esc(data.email)}.</p>
        </div>
      </div>
      <div class="p-4 mb-4 bg-white border border-green-200 rounded-lg">
        <p class="mb-1 text-xs font-semibold tracking-wide text-stone-500 uppercase">Código de seguimiento</p>
        <div class="flex flex-wrap items-center gap-3">
          <span id="complaint-reference-code" class="font-mono text-2xl font-bold tracking-wider text-stone-900">${esc(result.reference)}</span>
          <button type="button" id="complaint-copy-btn" class="px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors bg-amber-100 rounded-md hover:bg-amber-200 no-print">
            Copiar código
          </button>
        </div>
      </div>
      <div class="p-4 mb-4 text-sm bg-white border rounded-lg border-stone-200 text-stone-700">
        <p><span class="font-semibold">Nombre:</span> ${esc(data.full_name)}</p>
        <p><span class="font-semibold">Servicio:</span> ${esc(data.service_description)}</p>
        <p><span class="font-semibold">Detalle:</span> ${esc(data.detail)}</p>
        <p><span class="font-semibold">Pedido:</span> ${esc(data.request)}</p>
      </div>
      <button type="button" id="complaint-print-btn" class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white transition-colors bg-slate-900 rounded-lg hover:bg-slate-800 no-print">
        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="w-4 h-4" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1"/>
          <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2zm7 4V3a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v2zM4 9a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z"/>
        </svg>
        Imprimir constancia
      </button>
    `;
    this.messageContainer.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    const copyBtn = document.getElementById("complaint-copy-btn");
    if (copyBtn) {
      copyBtn.addEventListener("click", async () => {
        try {
          await navigator.clipboard.writeText(result.reference);
          copyBtn.textContent = "¡Copiado!";
          setTimeout(() => (copyBtn.textContent = "Copiar código"), 2000);
        } catch (e) {
          notifications.show(
            "No se pudo copiar automáticamente. Copia el código manualmente.",
            "warning"
          );
        }
      });
    }

    const printBtn = document.getElementById("complaint-print-btn");
    if (printBtn) {
      printBtn.addEventListener("click", () => window.print());
    }
  }

  clearMessage() {
    if (this.messageContainer) {
      this.messageContainer.style.display = "none";
      this.messageContainer.innerHTML = "";
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (this.isSubmitting) return;
    this.clearMessage();

    if (!this.form.reportValidity()) {
      this.showMessage(
        "Por favor completa los campos obligatorios antes de enviar.",
        "error"
      );
      return;
    }

    this.isSubmitting = true;
    this.setLoadingState(true);

    try {
      const data = this.collectFormData();
      const response = await fetch("/api/complaint", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await response.json();

      if (result.success) {
        this.showReceipt(data, result);
        this.form.reset();
        if (this.guardianFields) this.guardianFields.style.display = "none";
      } else {
        this.showMessage(
          result.message ||
            "No se pudo registrar tu solicitud. Por favor intenta nuevamente.",
          "error"
        );
      }
    } catch (error) {
      this.showMessage(
        "No se pudo registrar tu solicitud en este momento. Por favor intenta nuevamente o contáctanos directamente al +51-982-373-883.",
        "error"
      );
    } finally {
      this.isSubmitting = false;
      this.setLoadingState(false);
    }
  }
}

export const complaintFormManager = new ComplaintFormManager();
export default complaintFormManager;
